"use client"

import { useEffect, useMemo, useState } from "react"
import { useTranslations } from "next-intl"
import { Check, Flag, MoreVertical } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import type { DropdownSurface } from "@/lib/dropdown-styles"
import { ReportDialog } from "@/components/ui/report-dialog"
import type { ReportItemType } from "@/types/report"
import type { ImageRefInput } from "@/lib/storage/public-url"

interface ReportMenuProps {
  itemType: ReportItemType
  itemId: string
  itemTitle?: string
  currentUserId?: string
  reportedUserId?: string
  reportedUsername?: string
  /** AS STORED — `ReportDialog` resolves it (display + absolute-by-design column). */
  reportedAvatarUrl?: ImageRefInput | null
  triggerClassName?: string
  iconClassName?: string
  triggerIcon?: "menu" | "flag"
  contentAlign?: "start" | "center" | "end"
  disabled?: boolean
  contentClassName?: string
  itemClassName?: string
  surface?: DropdownSurface
}

const getKey = (userId: string, itemType: ReportItemType, itemId: string) => `report:${userId}:${itemType}:${itemId}`

export function ReportMenu({
  itemType,
  itemId,
  itemTitle,
  currentUserId,
  reportedUserId,
  reportedUsername,
  reportedAvatarUrl,
  triggerClassName,
  iconClassName,
  triggerIcon = "menu",
  contentAlign = "end",
  disabled = false,
  contentClassName,
  itemClassName,
  surface = "panel",
}: ReportMenuProps) {
  const t = useTranslations("report")
  const markIgnoreNextCardClick = () => {
    ;(globalThis as any).__kraahlIgnoreNextCardClickAt = Date.now()
  }

  const storageKey = useMemo(() => {
    if (!currentUserId) return null
    return getKey(currentUserId, itemType, itemId)
  }, [currentUserId, itemId, itemType])

  const [open, setOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [reported, setReported] = useState(false)

  useEffect(() => {
    if (!storageKey) {
      setReported(false)
      return
    }
    try {
      setReported(window.localStorage.getItem(storageKey) === "1")
    } catch {
      setReported(false)
    }
  }, [storageKey])

  const markReported = () => {
    setReported(true)
    if (!storageKey) return
    try {
      window.localStorage.setItem(storageKey, "1")
    } catch {}
  }

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={triggerIcon === "flag" ? t("report") : t("moreActions")}
            data-stop-prop
            className={triggerClassName}
            disabled={disabled}
            onPointerDown={(e) => {
              markIgnoreNextCardClick()
              e.stopPropagation()
            }}
            onMouseDown={(e) => {
              markIgnoreNextCardClick()
              e.stopPropagation()
            }}
          >
            <span className="relative inline-flex">
              {triggerIcon === "flag" ? <Flag className={iconClassName} /> : <MoreVertical className={iconClassName} />}
              {reported ? <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-red-500" /> : null}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={contentAlign} surface={surface} className={contentClassName}>
          <DropdownMenuItem
            disabled={reported}
            onSelect={(e) => {
              e.preventDefault()
              markIgnoreNextCardClick()
              e.stopPropagation()
              if (reported) return
              setOpen(false)
              requestAnimationFrame(() => {
                setDialogOpen(true)
              })
            }}
            className={cn("text-red-500 focus:text-red-500", itemClassName)}
          >
            {reported ? <Check className="mr-2 h-4 w-4" /> : <Flag className="mr-2 h-4 w-4" />}
            {reported ? t("reported") : t("report")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ReportDialog
        open={dialogOpen}
        onOpenChangeAction={(next) => {
          setDialogOpen(next)
          if (next) {
            setOpen(false)
          }
        }}
        itemType={itemType}
        itemId={itemId}
        itemTitle={itemTitle}
        currentUserId={currentUserId}
        reportedUserId={reportedUserId}
        reportedUsername={reportedUsername}
        reportedAvatarUrl={reportedAvatarUrl}
        alreadyReported={reported}
        onReported={markReported}
      />
    </>
  )
}
